import { type ReactNode } from 'react';
import { FieldError, Label as AriaLabel } from 'react-aria-components';

import { Stack, Box } from '../layout';
import { InfoTooltip } from '../tooltip';
import styles from './Field.module.css';
import { Text, ClearTextSlots } from '../typography';
import { VisuallyHidden } from '../visually-hidden';

export type Props = {
  label: string;
  isLabelVisuallyHidden?: boolean;
  description?: ReactNode;
  error?: ReactNode;
  isRequired?: boolean;
  /**
   * Short read-only explanation of what the field is for, shown in an (i) tooltip after the label.
   * Sits beside the label rather than inside it, so the tooltip's button is not part of the
   * label's accessible name.
   */
  labelInfo?: string;
};

type InternalProps = Props & {
  children?: ReactNode;
};

export function Field({
  label: labelFromProps,
  isLabelVisuallyHidden,
  description,
  error,
  isRequired,
  labelInfo,
  children,
}: InternalProps) {
  const label = (
    <Text size="md" color="gray-800" weight="medium" asChild>
      <AriaLabel>
        {labelFromProps}
        {isRequired ? ' *' : null}
      </AriaLabel>
    </Text>
  );

  return (
    <Box flex="none">
      <Stack gap="md">
        {isLabelVisuallyHidden ? (
          <VisuallyHidden>{label}</VisuallyHidden>
        ) : labelInfo ? (
          <Stack row align="center" gap="xs">
            {label}
            <span className={styles.labelInfo}>
              <InfoTooltip size="small" text={labelInfo} />
            </span>
          </Stack>
        ) : (
          label
        )}
        <Stack gap="sm">
          {children}
          {description ? (
            <Text slot="description" size="sm" color="gray-600">
              <ClearTextSlots>{description}</ClearTextSlots>
            </Text>
          ) : null}
          {error ? (
            <Text size="sm" color="red-500" asChild>
              <FieldError>{error}</FieldError>
            </Text>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}
